import { useQuery } from '@apollo/client';
import { ReceiptLong } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { DetailTextField } from '~/components/forms/details';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { formatDate } from '~/lib/datetime';
import { NetworkAuditLogDetailsDocument } from '../queries/details.generated';
import { Records } from './Records';

export const AuditLogDetails = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const params = useParams<{ auditLogId: string }>();
  const auditLogId = Number(params.auditLogId);

  const { data, loading, error } = useQuery(NetworkAuditLogDetailsDocument, {
    variables: { networkId: currentNetwork.id, auditLogId },
  });

  const auditLog = data?.network?.auditLog;

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<ReceiptLong />}
          titleText={t('pages.audit_logs.detail.title')}
        />
        <PageContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* General Section */}
            <div>
              <SettingsHead>{t('pages.audit_logs.detail.sections.general')}</SettingsHead>
              <SettingsBody>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('common.labels.action')}</SettingName>
                    <SettingDescription>
                      {t('pages.audit_logs.detail.fields.action_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField value={auditLog?.actionName ?? ''} disabled fullWidth />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('components.audit_logs.misc.user')}</SettingName>
                    <SettingDescription>
                      {t('pages.audit_logs.detail.fields.user_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField value={auditLog?.userName ?? ''} disabled fullWidth />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>{t('components.audit_logs.misc.date_time')}</SettingName>
                    <SettingDescription>
                      {t('pages.audit_logs.detail.fields.date_time_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      value={auditLog?.createdAt ? formatDate(auditLog.createdAt) : ''}
                      disabled
                      fullWidth
                    />
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </div>

            {/* Details Section */}
            <div>
              <SettingsHead>{t('common.labels.details')}</SettingsHead>
              <SettingsBody>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.audit_logs.detail.sections.payload')}</SettingName>
                    <SettingDescription>
                      {t('pages.audit_logs.detail.sections.payload_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: 'background.default',
                        border: 1,
                        borderColor: 'borders.light',
                        borderRadius: 1,
                        color: 'text.primary',
                        fontSize: '.9em',
                        width: '100%',
                        p: 1,
                        m: 0,
                        maxHeight: '400px',
                        overflow: 'auto',
                      }}
                    >
                      {JSON.stringify(auditLog?.payload || {}, null, 2)}
                    </Box>
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </div>

            {/* Affected Records Section */}
            <div>
              <SettingsHead>{t('pages.audit_logs.detail.sections.affected_records')}</SettingsHead>
              <Box
                sx={{
                  backgroundColor: 'white',
                  border: 1,
                  borderColor: 'borders.dark',
                  borderRadius: '0px 0px 5px 5px',
                  padding: '20px 24px 16px 16px',
                }}
              >
                <Records networkId={currentNetwork.id} auditLogId={auditLogId} />
              </Box>
            </div>
          </Box>
        </PageContainer>
      </>
    </LoadingPane>
  );
};
